/**
 * Notifications Page
 * Full list with pagination, filters, and actions
 */

import { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import FilterListIcon from "@mui/icons-material/FilterList";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useApiRequest from "utils/Apirequest";
import { useMaterialUIController } from "context";
import CustomMaterialTablePage from "components/custom/CustomMaterialTablePage";
import { convertUTCtoDubai } from "utils/Constant";
import Loader from "components/custom/Loader";
import { AutoTooltip } from "utils/Constant";

const NotificationsPage = () => {
  const apiRequest = useApiRequest();
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Pagination and sorting
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    { id: "createdAt", desc: true },
  ]);
  const [searchText, setSearchText] = useState("");

  // Action menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
  };

  const handleSearchChange = (value) => {
    const searchValue =
      typeof value === "string"
        ? value
        : value?.target?.value ?? "";

    setSearchText(searchValue);
    setPage(0);
  };

  useEffect(() => {
    setPage(pagination.pageIndex);
    setPageSize(pagination.pageSize);
  }, [pagination]);
  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      // setLoading(true);
      const params = new URLSearchParams({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        sortField: sorting[0]?.id || "createdAt",
        sortOrder: sorting[0]?.desc ? "desc" : "asc",
        search: searchText,
      });

      if (filterType !== "all") params.append("type", filterType);
      if (filterSeverity !== "all") params.append("severity", filterSeverity);
      if (unreadOnly) params.append("unreadOnly", "true");

      const { data } = await apiRequest(
        "get",
        `notifications/in-app/my-notifications?${params.toString()}`,
        null,
        {},
        {}
      );

      if (data?.success) {
        setNotifications(data.data || []);
        setTotalRows(data?.pagination?.total || 0);
        const list = data.data || [];
        setNotifications(list);
        setTotalRows(data?.pagination?.total || 0);

        const unread = list.filter(n => n.isRead === false).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, searchText, filterType, filterSeverity, unreadOnly]);

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await apiRequest("put", `notifications/in-app/${notificationId}/read`, {}, {}, {});
      fetchNotifications();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await apiRequest("put", "notifications/in-app/mark-all-read", {}, {}, {});
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await apiRequest("delete", `notifications/in-app/${notificationId}`, null, {}, {});
      fetchNotifications();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleActionClick = (event, notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "primary";
    }
  };

  const columns = [
    {
      header: "Status",
      accessorKey: "isRead",
      size: 200,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue() ? "Read" : "Unread"}
          color={cell.getValue() ? "default" : "primary"}
          size="small"
          variant={cell.getValue() ? "outlined" : "filled"}
        />
      ),
    },
    // {
    //   header: "Severity",
    //   accessorKey: "severity",
    //   size: 220,
    //   align: "center",
    //   muiTableHeadCellProps: { align: "center" },
    //   muiTableBodyCellProps: { align: "center" },
    //   Cell: ({ cell }) => (
    //     <Chip
    //       label={cell.getValue()?.toUpperCase() || "INFO"}
    //       color={getSeverityColor(cell.getValue())}
    //       size="small"
    //     />
    //   ),
    // },
    {
      header: "Type",
      accessorKey: "type",
      size: 250,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <MDTypography variant="caption" fontWeight="medium">
          {cell.getValue()?.replace(/_/g, " ").toUpperCase()}
        </MDTypography>
      ),
    },
    {
      header: "Title",
      accessorKey: "title",
      size: 350,
      Cell: ({ row }) => (
        <MDTypography
          variant="button"
          fontWeight={!row.original.read ? "bold" : "regular"}
        >
          {row.original.title}
        </MDTypography>
      ),
    },
    {
      header: "Message",
      accessorKey: "message",
      size: 300,
      Cell: ({ cell }) => (
        <MDTypography variant="caption" color="text">
          <AutoTooltip maxWidth={300}>{cell.getValue()}</AutoTooltip>
        </MDTypography>
      ),
    },
    {
      header: "Date",
      accessorKey: "createdAt",
      size: 180,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <MDTypography variant="caption">
          {convertUTCtoDubai(cell.getValue())}
        </MDTypography>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      size: 200,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <IconButton
          size="small"
          onClick={(e) => handleActionClick(e, row.original)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container spacing={3}>
          {/* Header with stats */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h4" fontWeight="medium">
                      Notifications
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6} textAlign="right">
                    {/* <MDButton
                      variant="outlined"
                      color={sidenavColor}
                      size="small"
                      startIcon={<FilterListIcon />}
                      onClick={() => setFilterDialogOpen(true)}
                      sx={{ mr: 1 }}
                    >
                      Filters
                    </MDButton> */}
                    <MDButton
                      variant="gradient"
                      color={sidenavColor}
                      size="small"
                      startIcon={<MarkEmailReadIcon />}
                      onClick={markAllAsRead}
                      disabled={unreadCount === 0}
                    >
                      Mark All as Read
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Notifications table */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                py={1}
                px={2}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
              >
                <MDTypography variant="h6" color="white">
                  Faults
                </MDTypography>
              </MDBox>

              {loading ? (
                <Loader />
              ) : (
                <CustomMaterialTablePage
                  columns={columns}
                  data={notifications}
                  darkMode={darkMode}
                  manualPagination={true}
                  manualFiltering={false}
                  rowCount={totalRows}
                  paginationChange={(newPage) => setPagination(newPage)}
                  soringChange={handleSortingChange}
                  page={page}
                  pagesize={pageSize}
                  paginataion={pagination}
                  sorting={sorting}
                  searchText={searchText}
                  searchChange={handleSearchChange}
                  onColumnFiltersChange={setColumnFilters}
                  onColumnVisibilityChange={setColumnVisibility}
                  state={{
                    pagination,
                    sorting,
                    columnFilters,
                    columnVisibility,
                  }}
                  globalFilterDebounceMs={2000}
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
      >
        {selectedNotification && !selectedNotification.isRead && (
          <MenuItem
            onClick={() => {
              markAsRead(selectedNotification._id);
              handleActionClose();
            }}
          >
            <MarkEmailReadIcon fontSize="small" sx={{ mr: 1 }} />
            Mark as Read
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            deleteNotification(selectedNotification._id);
            handleActionClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filter Notifications</DialogTitle>
        <DialogContent>
          <MDBox pt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    label="Type"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="charger_fault">Charger Fault</MenuItem>
                    <MenuItem value="charger_offline">Charger Offline</MenuItem>
                    <MenuItem value="session_complete">Session Complete</MenuItem>
                    <MenuItem value="reservation_reminder">Reservation Reminder</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={filterSeverity}
                    label="Severity"
                    onChange={(e) => setFilterSeverity(e.target.value)}
                  >
                    <MenuItem value="all">All Severities</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={unreadOnly}
                      onChange={(e) => setUnreadOnly(e.target.checked)}
                    />
                  }
                  label="Show Unread Only"
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setFilterType("all");
              setFilterSeverity("all");
              setUnreadOnly(false);
            }}
            color="secondary"
          >
            Clear Filters
          </MDButton>
          <MDButton
            onClick={() => setFilterDialogOpen(false)}
            variant="gradient"
            color={sidenavColor}
          >
            Apply
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default NotificationsPage;
